import { useEffect, useState } from "react";
import DashSidebar from "@/components/DashSidebar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Users, TrendingUp, DollarSign, Target, Plus, Trash2, TrendingDown, Filter as FilterIcon, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, sub, color = "#2C4C3B", testid }) => (
  <div className="card-soft p-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: color + "15" }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
    </div>
    <div className="text-sm text-[#5C6B62] font-medium">{label}</div>
    <div className="mt-1 font-display text-3xl font-semibold text-[#1A2E24]">{value}</div>
    {sub && <div className="mt-1 text-xs text-[#5C6B62]">{sub}</div>}
  </div>
);

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [spends, setSpends] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [attribution, setAttribution] = useState(null);
  const [amount, setAmount] = useState("");
  const [label, setLabel] = useState("");

  const load = async () => {
    try {
      const [m, s, f, a] = await Promise.all([
        api.get("/metrics/dashboard"),
        api.get("/ad-spend"),
        api.get("/metrics/funnel"),
        api.get("/metrics/attribution"),
      ]);
      setMetrics(m.data);
      setSpends(s.data);
      setFunnel(f.data);
      setAttribution(a.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addSpend = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ad-spend", { amount: parseFloat(amount), label });
      setAmount("");
      setLabel("");
      await load();
      toast.success("Ad spend added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const delSpend = async (id) => {
    await api.delete(`/ad-spend/${id}`);
    await load();
  };

  return (
    <div className="flex min-h-screen bg-[#F7F5F0]">
      <DashSidebar role="admin" />
      <main className="flex-1 lg:ml-0 ml-64 p-8 md:p-10">
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#1A2E24] mb-1">Dashboard</h1>
        <p className="text-[#5C6B62] mb-8">Your lead performance at a glance.</p>

        {metrics && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            <StatCard icon={Users} label="Total leads" value={metrics.total_leads} testid="stat-total-leads" />
            <StatCard
              icon={Target}
              label="Conversions"
              value={metrics.status_breakdown?.CONVERTED || 0}
              sub={`${metrics.conversion_rate}% conversion rate`}
              color="#9CAF88"
              testid="stat-conversions"
            />
            <StatCard
              icon={TrendingUp}
              label="Conversion rate"
              value={`${metrics.conversion_rate}%`}
              color="#E07A5F"
              testid="stat-conversion-rate"
            />
            <StatCard
              icon={DollarSign}
              label="Ad spend"
              value={`$${metrics.total_ad_spend.toLocaleString()}`}
              sub={`$${metrics.cost_per_lead} / lead · $${metrics.cost_per_conversion} / conv.`}
              color="#2C4C3B"
              testid="stat-ad-spend"
            />
          </div>
        )}

        {metrics && (
          <div className="grid md:grid-cols-2 gap-5 mb-8">
            <div className="card-soft p-6">
              <h3 className="font-display text-lg font-semibold text-[#1A2E24] mb-4">Status pipeline</h3>
              <div className="space-y-3">
                {Object.entries(metrics.status_breakdown).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-sm text-[#5C6B62]">{k}</span>
                    <span className="text-sm font-semibold text-[#1A2E24]">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card-soft p-6">
              <h3 className="font-display text-lg font-semibold text-[#1A2E24] mb-4">By care type</h3>
              <div className="space-y-3">
                {Object.entries(metrics.care_type_breakdown).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-sm text-[#5C6B62]">{k}</span>
                    <span className="text-sm font-semibold text-[#1A2E24]">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {funnel && (
          <div className="card-soft p-6 md:p-8 mb-8" data-testid="funnel-section">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="font-display text-xl font-semibold text-[#1A2E24]">Conversion funnel</h3>
                <p className="text-sm text-[#5C6B62] mt-1">Where families drop off — each step as a share of the top of funnel.</p>
              </div>
              <TrendingDown className="w-5 h-5 text-[#E07A5F]" />
            </div>
            <div className="space-y-3">
              {funnel.steps.map((s, i) => {
                const prevCount = i > 0 ? funnel.steps[i - 1].count : 0;
                const dropOff = prevCount > 0 ? Math.max(0, prevCount - s.count) : 0;
                return (
                  <div key={s.key} data-testid={`funnel-step-${s.key}`}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-[#1A2E24] font-medium">
                        <span className="text-[#9CAF88] mr-2">{String(i + 1).padStart(2, "0")}</span>
                        {s.label}
                      </span>
                      <span className="text-[#5C6B62]">
                        <strong className="text-[#1A2E24]">{s.count.toLocaleString()}</strong>
                        {s.conversion_from_previous != null && (
                          <span className="ml-2 text-xs">
                            {s.conversion_from_previous}% of prev
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="h-2.5 bg-[#F7F5F0] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#9CAF88] transition-all duration-500"
                        style={{ width: `${s.percent_of_top || 0}%` }}
                      />
                    </div>
                    {i > 0 && dropOff > 0 && (
                      <div className="text-xs text-[#E07A5F] mt-1">
                        — {dropOff.toLocaleString()} families exited here
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {attribution && attribution.rows.length > 0 && (
          <div className="card-soft p-6 md:p-8 mb-8" data-testid="attribution-section">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="font-display text-xl font-semibold text-[#1A2E24]">Attribution by source</h3>
                <p className="text-sm text-[#5C6B62] mt-1">Where your leads are coming from. Double down on what's working.</p>
              </div>
              <FilterIcon className="w-5 h-5 text-[#9CAF88]" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-[#5C6B62] border-b border-[#E2DFD8]">
                    <th className="py-2.5 font-medium">Source</th>
                    <th className="py-2.5 font-medium">Medium</th>
                    <th className="py-2.5 font-medium">Campaign</th>
                    <th className="py-2.5 font-medium text-right">Leads</th>
                    <th className="py-2.5 font-medium text-right">Completed</th>
                    <th className="py-2.5 font-medium text-right">Converted</th>
                    <th className="py-2.5 font-medium text-right">Conv rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2DFD8]">
                  {attribution.rows.map((r, i) => (
                    <tr key={i} className="hover:bg-[#F7F5F0]/60" data-testid={`attr-row-${i}`}>
                      <td className="py-2.5 text-[#1A2E24] font-medium">{r.utm_source || "(direct)"}</td>
                      <td className="py-2.5 text-[#5C6B62]">{r.utm_medium || "—"}</td>
                      <td className="py-2.5 text-[#5C6B62]">{r.utm_campaign || "—"}</td>
                      <td className="py-2.5 text-right text-[#1A2E24] font-medium">{r.leads}</td>
                      <td className="py-2.5 text-right text-[#5C6B62]">{r.completed}</td>
                      <td className="py-2.5 text-right text-[#1A2E24] font-medium">{r.converted}</td>
                      <td className="py-2.5 text-right">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.conversion_rate >= 10 ? "bg-[#9CAF88]/25 text-[#2C4C3B]" : r.conversion_rate >= 3 ? "bg-[#E07A5F]/15 text-[#C96B52]" : "bg-gray-100 text-gray-500"}`}>
                          {r.conversion_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {attribution && attribution.top_articles.length > 0 && (
          <div className="card-soft p-6 md:p-8 mb-8" data-testid="top-articles-section">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="font-display text-xl font-semibold text-[#1A2E24]">Top referring articles</h3>
                <p className="text-sm text-[#5C6B62] mt-1">Which articles are driving the quiz starts.</p>
              </div>
              <FileText className="w-5 h-5 text-[#2C4C3B]" />
            </div>
            <div className="space-y-2.5">
              {attribution.top_articles.map((a) => (
                <div key={a.slug || "unknown"} className="flex items-center justify-between p-3 rounded-xl bg-[#F7F5F0]" data-testid={`top-article-${a.slug || "unknown"}`}>
                  <div className="text-sm text-[#1A2E24] font-medium truncate max-w-lg">{(a.slug || "(unknown)").replace(/-/g, " ")}</div>
                  <div className="flex items-center gap-6 text-xs text-[#5C6B62]">
                    <span><strong className="text-[#1A2E24]">{a.leads}</strong> leads</span>
                    <span><strong className="text-[#1A2E24]">{a.converted}</strong> converted</span>
                    <span className="text-[#9CAF88] font-medium">{a.conversion_rate}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card-soft p-6 md:p-8">
          <h3 className="font-display text-xl font-semibold text-[#1A2E24] mb-1">Ad Spend (ROI tracking)</h3>
          <p className="text-sm text-[#5C6B62] mb-5">
            Add your ad spend and we'll calculate your cost per lead and cost per conversion.
          </p>
          <form onSubmit={addSpend} className="grid md:grid-cols-3 gap-3 mb-6">
            <div>
              <Label className="text-[#2C4C3B] text-sm">Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-1 h-11 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                required
                data-testid="spend-amount"
              />
            </div>
            <div>
              <Label className="text-[#2C4C3B] text-sm">Label</Label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="mt-1 h-11 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                placeholder="Facebook Feb 2026"
                data-testid="spend-label"
              />
            </div>
            <div className="flex items-end">
              <Button type="submit" className="bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl h-11 w-full" data-testid="spend-submit">
                <Plus className="w-4 h-4 mr-1.5" /> Add spend
              </Button>
            </div>
          </form>

          <div className="divide-y divide-[#E2DFD8]">
            {spends.length === 0 && <div className="text-sm text-[#5C6B62]">No ad spend recorded yet.</div>}
            {spends.map((s) => (
              <div key={s.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="font-medium text-[#1A2E24]">${s.amount.toLocaleString()}</div>
                  <div className="text-xs text-[#5C6B62]">{s.label || "—"} · {new Date(s.created_at).toLocaleDateString()}</div>
                </div>
                <button
                  onClick={() => delSpend(s.id)}
                  className="text-[#E07A5F] hover:text-[#C96B52]"
                  data-testid={`spend-delete-${s.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
